import React from 'react'
import {
  Box,
  Grid,
  Checkbox,
  Typography,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip
} from '@mui/material'
import { Item } from 'hooks/useProduct'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import AssembleOptions from 'components/AssembleOptions'
import { useTranslation } from 'react-i18next'

interface CartItemComponentProps {
  item: Item
  stock?: number
  isSelected: boolean
  isBatchEditActive: boolean
  onSelect: (isSelected: boolean) => void
  onUpdate: (updateParams: {
    quantity?: number
    isAssembled?: boolean
    hingeDirection?: string
  }) => void
  onDelete: () => void
}

const CartItem: React.FC<CartItemComponentProps> = ({
  item,
  stock,
  isSelected,
  isBatchEditActive,
  onSelect,
  onUpdate,
  onDelete
}) => {
  const { t } = useTranslation()
  return (
    <Box sx={{ my: '30px' }}>
      <Grid container>
        <Grid item xs={4}>
          <Box display='flex' alignItems='center'>
            {isBatchEditActive && (
              <Checkbox
                checked={isSelected}
                onChange={e => onSelect(e.target.checked)}
              />
            )}

            <Box
              sx={{
                width: '200px',
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Box
                component='img'
                src={`${PRODUCT_IMAGE_BASE_URL}${item.imageIndex}.png`}
                alt={item.sku}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={8}>
          <Grid container>
            <Grid item xs={8}>
              <Box sx={{ ml: 2, flexGrow: 1 }}>
                <Typography variant='h3'>{item.sku}</Typography>
                <Typography variant='h2'>
                  ${Number(item.price).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                display='flex'
                justifyContent='flex-end'
                alignItems='center'
                gap={2}
              >
                {stock === undefined ? (
                  <CircularProgress size={24} />
                ) : stock === 0 ? (
                  <Chip
                    label={t('productDetail.outOfStock')}
                    size='small'
                    sx={{
                      backgroundColor: 'rgba(211,47,47,0.08)',
                      color: 'error.main',
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '13px',
                      height: '28px'
                    }}
                  />
                ) : (
                  <FormControl variant='outlined'>
                    <Select
                      value={item.quantity}
                      onChange={e =>
                        onUpdate({
                          quantity: Number(e.target.value)
                        })
                      }
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '.MuiSelect-icon': { color: 'primary.main' }
                      }}
                    >
                      {Array.from(
                        {
                          length: item.qtyRestrictions
                            ? Math.min(item.qtyRestrictions, stock)
                            : stock
                        },
                        (_, i) => i + 1
                      ).map(q => (
                        <MenuItem
                          key={q}
                          value={q}
                          sx={{
                            justifyContent: 'center'
                          }}
                        >
                          <Typography variant='h2'>{q}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Typography variant='h2'>
                  ${(item.price * item.quantity).toFixed(2)}
                </Typography>
              </Box>
              <Box
                display='flex'
                justifyContent='flex-end'
                alignItems='center'
                mt={1}
              >
                <Button
                  variant='text'
                  sx={{
                    color: 'primary.main',
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: '400',
                    lineHeight: '22.4px',
                    textAlign: 'left'
                  }}
                  onClick={onDelete}
                >
                  Remove
                </Button>
              </Box>
            </Grid>

            {item.afmx !== 0 && (
              <AssembleOptions
                isHingeable={item.isHingeable}
                isAssembled={item.isAssembled}
                hingeDirection={item.hingeDirection ?? 'LEFT'}
                onUpdate={onUpdate}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ width: '100%', mt: 2, mb: 5 }} />
    </Box>
  )
}

export default CartItem
